/**
 * CSV Export Utility
 *
 * Generates CSV files from decrypted event data.
 * All processing happens client-side (zero-knowledge).
 */

import { format } from 'date-fns';
import { EventType } from '../types';

export interface DecryptedEvent {
  id: string;
  type: EventType;
  timestamp: Date;
  data: Record<string, unknown>;
  notes?: string;
}

export interface CSVExportOptions {
  babyName: string;
  events: DecryptedEvent[];
  includeNotes?: boolean;
}

/**
 * Format event data as a readable string for CSV
 */
function formatEventData(type: EventType, data: Record<string, unknown>): string {
  const parts: string[] = [];

  switch (type) {
    case EventType.FEED:
      if (data.feedType) parts.push(`Type: ${data.feedType}`);
      if (data.side) parts.push(`Side: ${data.side}`);
      if (data.duration) parts.push(`Duration: ${data.duration}min`);
      if (data.amount) parts.push(`Amount: ${data.amount}ml`);
      break;

    case EventType.NAPPY:
      if (data.wet) parts.push('Wet');
      if (data.dirty) parts.push('Dirty');
      if (data.rash) parts.push('Rash noted');
      break;

    case EventType.SLEEP:
      if (data.sleepType) parts.push(`Type: ${data.sleepType}`);
      if (data.duration) parts.push(`Duration: ${data.duration}min`);
      break;

    case EventType.MEDICATION:
      if (data.medicationName) parts.push(`Name: ${data.medicationName}`);
      if (data.dose) parts.push(`Dose: ${data.dose}`);
      break;

    case EventType.WEIGHT:
      if (data.weight) parts.push(`Weight: ${data.weight}${data.unit || 'kg'}`);
      if (data.height) parts.push(`Height: ${data.height}cm`);
      break;

    case EventType.TEMPERATURE:
      if (data.temperature) parts.push(`Temp: ${data.temperature}°${data.tempUnit || 'C'}`);
      if (data.method) parts.push(`Method: ${data.method}`);
      break;

    case EventType.TUMMY_TIME:
      if (data.duration) parts.push(`Duration: ${data.duration}min`);
      if (data.mood) parts.push(`Mood: ${data.mood}`);
      break;

    case EventType.PUMP:
      if (data.leftAmount) parts.push(`Left: ${data.leftAmount}ml`);
      if (data.rightAmount) parts.push(`Right: ${data.rightAmount}ml`);
      break;

    case EventType.BOTTLE:
      if (data.bottleType) parts.push(`Type: ${data.bottleType}`);
      if (data.amountOffered) parts.push(`Amount: ${data.amountOffered}ml`);
      break;

    case EventType.SPIT_UP:
      if (data.severity) parts.push(`Severity: ${data.severity}`);
      break;

    case EventType.JOURNAL:
      if (data.title) parts.push(`Title: ${data.title}`);
      if (data.isMilestone) parts.push('Milestone');
      break;
  }

  return parts.join('; ') || '-';
}

/**
 * Escape a value for CSV (handle commas, quotes, newlines)
 */
function escapeCSV(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/**
 * Generate CSV content from events
 */
export function generateCSV(options: CSVExportOptions): string {
  const { babyName, events, includeNotes = true } = options;

  // Header row
  const headers = ['Date', 'Time', 'Event Type', 'Details'];
  if (includeNotes) {
    headers.push('Notes');
  }

  const rows: string[][] = [];

  // Sort events by timestamp (newest first)
  const sortedEvents = [...events].sort(
    (a, b) => b.timestamp.getTime() - a.timestamp.getTime()
  );

  for (const event of sortedEvents) {
    const row = [
      format(event.timestamp, 'yyyy-MM-dd'),
      format(event.timestamp, 'HH:mm:ss'),
      event.type,
      formatEventData(event.type, event.data),
    ];

    if (includeNotes) {
      row.push(event.notes || '');
    }

    rows.push(row);
  }

  // Build CSV content
  const csvLines = [
    `# ${babyName}'s Activity Log`,
    `# Exported: ${format(new Date(), 'yyyy-MM-dd HH:mm:ss')}`,
    `# Total Events: ${events.length}`,
    '',
    headers.map(escapeCSV).join(','),
    ...rows.map((row) => row.map(escapeCSV).join(',')),
  ];

  return csvLines.join('\n');
}

/**
 * Trigger a CSV file download
 */
export function downloadCSV(content: string, filename: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up
  URL.revokeObjectURL(url);
}

/**
 * Export events to CSV and trigger download
 */
export function exportToCSV(options: CSVExportOptions): void {
  const csvContent = generateCSV(options);
  const filename = `${options.babyName.replace(/\s+/g, '_')}_activity_${format(new Date(), 'yyyy-MM-dd')}.csv`;
  downloadCSV(csvContent, filename);
}
